/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";

import { usePlayerTranslation } from "../../shared/context/PlayerTranslationContext";
import { useScreenOrientation } from "../../shared/hooks/useScreenOrientation";
import { PlayerLiveScreensConfig } from "../../shared/types";
import { formatDurationMs } from "../../shared/utils/time";
import { ClosePlayerButtonControl } from "../Interface";

import "./LiveScreen.scss";

export type LiveScreenType = "starting" | "ended";

interface LiveScreenProps {
  type: LiveScreenType;
  config: PlayerLiveScreensConfig;
  startDateTime?: Date | string;
  getCurrentTimeMs?: () => number;
}

const getDateTime = (date?: Date | string): number | undefined => {
  if (!date) {
    return undefined;
  }

  const time = new Date(date).getTime();
  return Number.isNaN(time) ? undefined : time;
};

export const LiveScreen = (props: LiveScreenProps) => {
  const { type, config, startDateTime, getCurrentTimeMs } = props;
  const { t } = usePlayerTranslation();
  const orientation = useScreenOrientation();
  const [now, setNow] = useState(() => getCurrentTimeMs?.() ?? Date.now());
  const [videoFailed, setVideoFailed] = useState(false);

  const screenConfig = config[type];
  const background =
    screenConfig?.[orientation] ||
    screenConfig?.landscape ||
    screenConfig?.portrait;
  const image = background?.image;
  const video = background?.video;
  const videoUrl = video?.url;
  const startTime = useMemo(() => getDateTime(startDateTime), [startDateTime]);
  const countdown = startTime
    ? formatDurationMs(Math.max(startTime - now, 0), "HH:mm:ss")
    : undefined;
  const showStatusInfo = config.showStatusInfo !== false;
  const showCountdown =
    type === "starting" && config.showCountdownTimer && !!countdown;

  useEffect(() => {
    setVideoFailed(false);
  }, [videoUrl]);

  useEffect(() => {
    if (type !== "starting" || !config.showCountdownTimer || !startTime) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setNow(getCurrentTimeMs?.() ?? Date.now());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [config.showCountdownTimer, getCurrentTimeMs, startTime, type]);

  return (
    <div
      className={classNames("LiveScreen", `LiveScreen--${type}`)}
      data-orientation={orientation}
      data-screen={type}
    >
      <div className="LiveScreen__BackgroundLayer" aria-hidden="true">
        {image?.url && (
          <img
            className="LiveScreen__Background LiveScreen__Background--image"
            src={image.url}
            alt=""
          />
        )}
        {videoUrl && !videoFailed && (
          <video
            className="LiveScreen__Background LiveScreen__Background--video"
            autoPlay
            loop
            muted
            playsInline
            onError={() => setVideoFailed(true)}
          >
            <source src={videoUrl} type={video?.contentType} />
          </video>
        )}
      </div>
      <div className="LiveScreen__TopSection">
        <ClosePlayerButtonControl />
      </div>
      {showStatusInfo && (
        <div className="LiveScreen__Content">
          {type === "starting" ? (
            <>
              <h1 className="LiveScreen__Header">
                {t("PLAYER_LIVE_STARTING_HEADER")}
              </h1>
              <p className="LiveScreen__Description">
                {t("PLAYER_LIVE_STARTING_DESCRIPTION")}
              </p>
              {showCountdown && (
                <time
                  className="LiveScreen__Countdown"
                  dateTime={
                    startTime ? new Date(startTime).toISOString() : undefined
                  }
                >
                  {countdown}
                </time>
              )}
            </>
          ) : (
            <>
              <h1 className="LiveScreen__Header">
                {t("PLAYER_LIVE_ENDED_HEADER")}
              </h1>
              <p className="LiveScreen__Description">
                {t("PLAYER_LIVE_ENDED_DESCRIPTION")}
              </p>
              <p className="LiveScreen__Description">
                {t("PLAYER_LIVE_ENDED_INVITATION")}
              </p>
            </>
          )}
        </div>
      )}
      {!showStatusInfo && showCountdown && (
        <div className="LiveScreen__CountdownContainer">
          <time
            className="LiveScreen__Countdown"
            dateTime={startTime ? new Date(startTime).toISOString() : undefined}
          >
            {countdown}
          </time>
        </div>
      )}
    </div>
  );
};
